import { Alert, Select, Text } from '@mantine/core';
import { useQuery } from '@tanstack/react-query';
import { rawApi } from '../api/client';
import { listKey } from '../api/queryKeys';
import { useLocationId } from '../hooks/useSeedLocation';
import { formatDateTime, formatMoney } from '../lib/format';

/**
 * "Which register did that cash come out of?" — asked once, in one place, by every screen that pays
 * somebody in cash.
 *
 * There are two of those screens and they had the same defect: a supplier bill settled with "Cash
 * out of the register" and a driver's commission counted out at the bar both recorded a payment and left
 * the drawer alone. The money went, the drawer did not know, and at close the drawer came up SHORT
 * by exactly what had been handed over — against whoever was standing at it, who had done nothing
 * wrong.
 *
 * The register cannot be guessed from the office, so it is asked. And the answer is sometimes "there
 * isn't one": the gas man comes at eleven in the morning, before anybody has opened a drawer. That
 * is not an error to hide — it means the money came from the safe, the float box or a pocket, and
 * the honest record is a different payment method. The supplier form now has a real one for the
 * boxes — cash out of the float box or the safe, recorded on the box's own ledger — so where the
 * hosting form offers it, the alert below points at it by name instead of only stopping.
 */

const OPEN_REGISTERS_PATH = '/admin/v1/cash/open-drawers';

export interface OpenRegister {
  drawerSessionId: string;
  registerName: string;
  ownerName: string | null;
  openedAt: string;
  expected: { usdMinor: number; hnlMinor: number };
}

/**
 * The registers that are open right now.
 *
 * `enabled` so the question is only asked while a form is actually offering cash — the answer goes
 * stale the moment somebody counts a drawer off, and a form left open on a desk all afternoon
 * should not be holding this morning's answer.
 */
export function useOpenRegisters(enabled: boolean) {
  const locationId = useLocationId();
  const query = useQuery({
    queryKey: listKey(OPEN_REGISTERS_PATH, { locationId }),
    queryFn: () => rawApi<OpenRegister[]>('GET', `${OPEN_REGISTERS_PATH}?locationId=${locationId ?? ''}`),
    enabled: enabled && Boolean(locationId),
    staleTime: 15_000,
  });
  return {
    registers: query.data ?? [],
    loading: query.isLoading,
    /** True once we know the answer and the answer is "no drawer is open". */
    noneOpen: !query.isLoading && query.isSuccess && (query.data ?? []).length === 0,
  };
}

/** What one register is holding in the money being paid out, so an impossible choice is visible. */
function heldIn(register: OpenRegister, currency: string): number {
  return currency === 'USD' ? register.expected.usdMinor : register.expected.hnlMinor;
}

/**
 * The picker, plus the two things it has to be able to say instead of offering a choice.
 *
 * A single open register is preselected by the caller and still shown, because "which drawer will be
 * short tonight" is a fact worth reading before pressing the button, not an implementation detail.
 */
export function CashOutOfRegister({
  registers,
  loading,
  value,
  onChange,
  currency,
  amountMinor,
  disabled,
  boxOffered,
}: {
  registers: OpenRegister[];
  loading: boolean;
  value: string | null;
  onChange: (drawerSessionId: string | null) => void;
  currency: string;
  /** What is about to be handed over, so a register that cannot cover it is flagged before saving. */
  amountMinor: number;
  disabled?: boolean;
  /**
   * The hosting form offers a real cash-out-of-a-box method, so the no-drawer alert may name it.
   * The forms that show this picker have different method lists, and a sentence naming a control
   * that is only on one of them is a defect on the other — so the caller says, and only then does
   * the alert point at the door.
   */
  boxOffered?: boolean;
}) {
  if (loading) {
    return (
      <Text size="sm" c="dimmed">
        Checking which registers are open…
      </Text>
    );
  }

  /*
   * "OPEN THE REGISTER FIRST" — an instruction with no screen behind it, and the owner is the one
   * reading it. A drawer is opened at the register by whoever is starting the shift; there is no button
   * for it here and there is not supposed to be one, because that gesture belongs to the bar and not
   * to the office (`docs/domain/one-person-on-a-shift.md`, "Whose gesture is whose"). So the sentence
   * sent him looking for something that does not exist, in the middle of paying the gas man at eleven
   * in the morning.
   *
   * What is left is the true half: the money came from somewhere else, and saying which is the
   * honest record. Where the form offers the from-the-box method (`boxOffered`), the alert names it
   * by its exact label, because "record it that way instead" with no way to do so was this alert's
   * own defect for as long as no such method existed. Where the form does not — a driver's payout
   * has no box method — the sentence stays general rather than naming a control that is not there.
   */
  if (registers.length === 0) {
    return (
      <Alert color="yellow" variant="light" title="No register is open">
        Nobody has a drawer open at the moment, so there is no register this money can have come out of — a
        drawer is opened at the bar, at the start of a shift.{' '}
        {boxOffered
          ? 'If the cash came out of the float box or the safe, pick “Cash out of the float box or safe” above — the payment goes on that box’s own ledger, and no drawer needs to be open. Cash from your own pocket is “Some other way”.'
          : 'If the cash came from the safe, the change box or your own pocket, record it that way instead: putting it against a drawer would leave whoever counts that register tonight short by this much, with nothing on the record to explain it.'}
      </Alert>
    );
  }

  const chosen = registers.find((t) => t.drawerSessionId === value);
  const short = chosen !== undefined && amountMinor > heldIn(chosen, currency);

  return (
    <>
      <Select
        label="Which register it came out of"
        description="The drawer this cash left, so it still counts off level tonight"
        required
        data={registers.map((t) => ({
          value: t.drawerSessionId,
          label: t.ownerName
            ? `${t.registerName} · ${t.ownerName} since ${formatDateTime(t.openedAt)}`
            : `${t.registerName} · open since ${formatDateTime(t.openedAt)}`,
        }))}
        value={value}
        onChange={onChange}
        allowDeselect={false}
        comboboxProps={{ withinPortal: true }}
        disabled={disabled}
      />
      {chosen && (
        <Text size="sm" c={short ? 'red' : 'dimmed'} mt={-8}>
          {short
            ? `That register is only holding ${formatMoney(heldIn(chosen, currency), currency)} — take the rest out of the float box or the safe first, and count it in.`
            : `Holding ${formatMoney(heldIn(chosen, currency), currency)} right now.`}
        </Text>
      )}
    </>
  );
}
